import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import AuthUtil from '../../redux/AuthUtil';
import { STRIPE as STRIPE_ENDPOINTS } from '../global/Server/endpoints';
import PayWithStripeDialog from './PayWithStripeDialog';

// Inlined keyframes for the success panel. Two effects: (1) pop the
// green check bubble in with a slight overshoot for the "tactile"
// feel of a successful transaction; (2) a soft radial ripple behind
// the bubble that continues pulsing so the screen feels alive after
// the initial entry settles.
const SUCCESS_KEYFRAMES = `
@keyframes nubeSuccessPop {
  0%   { transform: scale(0.4); opacity: 0; }
  55%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes nubeSuccessRipple {
  0%   { transform: scale(1);   opacity: 0.55; }
  100% { transform: scale(1.6); opacity: 0; }
}
@keyframes nubeSuccessSlideUp {
  0%   { transform: translateY(10px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
}
`;

/**
 * Saved-card payment picker for the "Pay" action on UNPAID Payment History
 * rows. Shows the company's saved cards and lets the admin charge one
 * directly without re-entering card details.
 *
 * Flow:
 *   1. On open, fetch /stripe/listSavedCards.
 *   2. User clicks a card → POST /stripe/createPayNow with paymentMethodId,
 *      amount, paymentId (the UNPAID row id), confirm=true. Server creates
 *      AND confirms the PaymentIntent inline.
 *   3. status='succeeded' → close + onSuccess (parent refreshes history).
 *      status='requires_action' → 3DS challenge; v1 surfaces a clear error
 *      ("This card requires verification — try another card or add a new
 *      one"). Full 3DS-in-dialog handling is a separate iteration.
 *   4. Failure → inline error, picker stays open so admin can pick another
 *      card.
 *   5. "Use a different card" button at the bottom opens PayWithStripeDialog
 *      with PaymentElement for a one-off new-card payment.
 */
class SavedCardPaymentDialog extends React.Component {
  state = {
    loading: false,
    methods: [],
    error: null,
    charging: null, // id of the PM currently being charged
    done: false,
    newCardOpen: false, // new-card pay flow (auto-opens when no saved cards)
  };

  _mounted = false;

  componentDidMount() {
    this._mounted = true;
    if (this.props.open) this.fetchMethods();
  }

  componentDidUpdate(prev) {
    if (!prev.open && this.props.open) {
      // Fresh state every open so a previous error/done state doesn't leak.
      this.setState(
        {
          loading: false,
          methods: [],
          error: null,
          charging: null,
          done: false,
          newCardOpen: false,
        },
        this.fetchMethods
      );
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  setSafe = (next) => {
    if (this._mounted) this.setState(next);
  };

  fetchMethods = async () => {
    this.setSafe({ loading: true, error: null });
    try {
      const res = await AuthUtil.postRequest(
        STRIPE_ENDPOINTS.LIST_SAVED_CARDS,
        {
          companyId: this.props.companyId,
        }
      );
      if (res?.data?.success) {
        // Sort: default first, then by brand label.
        const methods = (res.data.methods || []).slice().sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return 0;
        });
        // No saved cards → skip the picker entirely and drop the user
        // straight into the new-card pay form. The PaymentElement there
        // carries a "Save card for future payments" checkbox so a single
        // submit can charge AND save. The previous flow forced a separate
        // SetupIntent detour ("add card first, then come back"), which is
        // what the user explicitly rejected.
        this.setSafe({
          methods,
          loading: false,
          newCardOpen: methods.length === 0,
        });
      } else {
        this.setSafe({
          loading: false,
          error: res?.data?.message || 'Could not load saved cards.',
        });
      }
    } catch (e) {
      this.setSafe({ loading: false, error: 'Could not load saved cards.' });
    }
  };

  payWithSavedCard = async (method) => {
    if (this.state.charging) return; // single in-flight charge
    this.setSafe({ charging: method.id, error: null });
    try {
      const res = await AuthUtil.postRequest(STRIPE_ENDPOINTS.CREATE_PAY_NOW, {
        companyId: this.props.companyId,
        paymentMethodId: method.id,
        amount: this.props.amount,
        paymentId: this.props.paymentId,
        confirm: true,
      });
      const data = res?.data;
      if (data?.success && data?.status === 'succeeded') {
        this.setSafe({ charging: null, done: true });
        if (this.props.onSuccess) this.props.onSuccess(data);
        return;
      }
      if (data?.status === 'requires_action') {
        this.setSafe({
          charging: null,
          error:
            'This card needs 3D Secure verification. Try a different saved card or add a new one.',
        });
        return;
      }
      this.setSafe({
        charging: null,
        error: data?.message || 'Payment failed. Please try a different card.',
      });
    } catch (e) {
      this.setSafe({
        charging: null,
        error: 'Payment failed. Please try a different card.',
      });
    }
  };

  brandLabel = (m) => {
    if (m.type === 'link' || m.walletType === 'link') return 'Link';
    if (m.walletType === 'apple_pay') return 'Apple Pay';
    if (m.walletType === 'google_pay') return 'Google Pay';
    const b = (m.brand || 'card').toString();
    return b.charAt(0).toUpperCase() + b.slice(1);
  };

  brandIcon = (m) => {
    const sz = { fontSize: 22 };
    if (m.type === 'link' || m.walletType === 'link')
      return <CreditCardIcon style={{ ...sz, color: '#00d66f' }} />;
    if (m.walletType === 'apple_pay')
      return <AppleIcon style={{ ...sz, color: '#0f172a' }} />;
    if (m.walletType === 'google_pay')
      return <AndroidIcon style={{ ...sz, color: '#4285F4' }} />;
    return <CreditCardIcon style={{ ...sz, color: '#4338ca' }} />;
  };

  detailLine = (m) => {
    if (m.type === 'link' || m.walletType === 'link')
      return m.linkEmail || 'Link account';
    return `Expires ${String(m.expMonth || '').padStart(2, '0')}/${
      m.expYear || ''
    }`;
  };

  render() {
    const { open, onClose, amount } = this.props;
    const { loading, methods, error, charging, done, newCardOpen } = this.state;
    const amtLabel = amount != null ? `$${Number(amount).toFixed(2)}` : '';

    return (
      <>
        <Dialog
          open={!!open && !newCardOpen}
          onClose={() => !charging && onClose && onClose()}
          maxWidth="xs"
          fullWidth
          PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
        >
          {/* Header — hidden on the success screen so the post-pay state
              is a clean "Payment successful" panel instead of carrying
              the now-misleading "Pay with saved card $X" amount header. */}
          {!done && (
            <Box
              style={{
                padding: '18px 22px 8px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    color: '#8a9cc5',
                    textTransform: 'uppercase',
                  }}
                >
                  Pay with saved card
                </Typography>
                <Typography
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#0f172a',
                    marginTop: 2,
                  }}
                >
                  {amtLabel}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => !charging && onClose && onClose()}
                disabled={!!charging}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          <DialogContent
            style={{
              padding: done ? '4px 22px 0' : '4px 22px 8px',
              borderTop: done ? 'none' : undefined,
            }}
          >
            {done && (
              <>
                <style>{SUCCESS_KEYFRAMES}</style>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '28px 16px 8px',
                  }}
                >
                  {/* Animated check bubble — gradient fill, soft shadow,
                      pulsing radial halo behind it. */}
                  <Box
                    style={{
                      position: 'relative',
                      width: 92,
                      height: 92,
                      marginBottom: 22,
                    }}
                  >
                    <Box
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        background:
                          'radial-gradient(circle, rgba(16,185,129,0.22), transparent 70%)',
                        animation:
                          'nubeSuccessRipple 1.8s ease-out 0.35s infinite',
                      }}
                    />
                    <Box
                      style={{
                        position: 'absolute',
                        inset: 10,
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow:
                          '0 14px 30px -10px rgba(16,185,129,0.55), 0 4px 10px rgba(0,0,0,0.05)',
                        animation:
                          'nubeSuccessPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}
                    >
                      <CheckIcon style={{ fontSize: 44, color: '#fff' }} />
                    </Box>
                  </Box>

                  <Typography
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: '#0f172a',
                      letterSpacing: '-0.02em',
                      animation: 'nubeSuccessSlideUp 0.4s ease-out 0.15s both',
                    }}
                  >
                    Payment Successful
                  </Typography>

                  {amount != null && (
                    <Typography
                      style={{
                        fontSize: 30,
                        fontWeight: 800,
                        color: '#15803d',
                        letterSpacing: '-0.03em',
                        marginTop: 4,
                        animation:
                          'nubeSuccessSlideUp 0.4s ease-out 0.22s both',
                      }}
                    >
                      {amtLabel}
                    </Typography>
                  )}

                  <Typography
                    style={{
                      fontSize: 13,
                      color: '#6b7280',
                      textAlign: 'center',
                      lineHeight: 1.6,
                      maxWidth: 290,
                      marginTop: 10,
                      animation: 'nubeSuccessSlideUp 0.4s ease-out 0.3s both',
                    }}
                  >
                    Your subscription has been updated.
                  </Typography>
                </Box>
              </>
            )}

            {!done && loading && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                py={3}
              >
                <CircularProgress size={22} />
                <Typography
                  style={{ marginLeft: 10, color: '#6b7280', fontSize: 13 }}
                >
                  Loading saved cards…
                </Typography>
              </Box>
            )}

            {!done && !loading && error && (
              <Box
                style={{
                  padding: '10px 12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 10,
                  color: '#b91c1c',
                  fontSize: 13,
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <ErrorOutlineIcon style={{ fontSize: 18, marginTop: 1 }} />
                <span>{error}</span>
              </Box>
            )}

            {!done && !loading && methods.length > 0 && (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {methods.map((m) => {
                  const isCharging = charging === m.id;
                  return (
                    <Button
                      key={m.id}
                      onClick={() => this.payWithSavedCard(m)}
                      disabled={!!charging}
                      style={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        padding: '12px 14px',
                        borderRadius: 12,
                        border: m.isDefault
                          ? '1.5px solid #4338ca'
                          : '1px solid #e2e8f0',
                        background: m.isDefault ? '#f8f9ff' : '#fff',
                        opacity: charging && !isCharging ? 0.45 : 1,
                      }}
                    >
                      <Box
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 8,
                          flexShrink: 0,
                          background: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: 10,
                        }}
                      >
                        {this.brandIcon(m)}
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: '#0f172a',
                            }}
                          >
                            {this.brandLabel(m)}
                            {m.last4 ? ` •••• ${m.last4}` : ''}
                          </Typography>
                          {m.isDefault && (
                            <StarIcon
                              style={{ fontSize: 14, color: '#f59e0b' }}
                            />
                          )}
                        </Box>
                        <Typography
                          style={{
                            fontSize: 12,
                            color: '#6b7280',
                            marginTop: 1,
                          }}
                        >
                          {this.detailLine(m)}
                        </Typography>
                      </Box>
                      {isCharging ? (
                        <CircularProgress size={18} style={{ marginLeft: 6 }} />
                      ) : (
                        <ChevronRightIcon
                          style={{
                            fontSize: 22,
                            color: '#94a3b8',
                            marginLeft: 6,
                          }}
                        />
                      )}
                    </Button>
                  );
                })}
              </Box>
            )}
          </DialogContent>

          {!done && methods.length > 0 && (
            <DialogActions style={{ padding: '8px 22px 18px' }}>
              <Button
                onClick={() => this.setSafe({ newCardOpen: true })}
                disabled={!!charging}
                startIcon={<AddIcon />}
                style={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#4338ca',
                  fontSize: 13,
                  borderRadius: 10,
                }}
              >
                Use a different card
              </Button>
            </DialogActions>
          )}

          {done && (
            <DialogActions
              style={{ padding: '16px 22px 22px', justifyContent: 'center' }}
            >
              <Button
                onClick={() => onClose && onClose()}
                variant="contained"
                disableElevation
                fullWidth
                style={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 14,
                  borderRadius: 12,
                  padding: '11px 24px',
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  boxShadow: '0 6px 16px -4px rgba(16,185,129,0.5)',
                  letterSpacing: '0.01em',
                  animation: 'nubeSuccessSlideUp 0.4s ease-out 0.4s both',
                }}
              >
                Done
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* New-card pay flow. Opened two ways: (1) explicitly via the
            "Use a different card" button when saved cards exist, (2)
            automatically by fetchMethods when the company has no saved
            cards on file. Carries the "Save for future payments" checkbox
            so a single submit can charge + (opt-in) save. If the user
            cancels and had no saved cards to fall back on, close the
            outer picker too — otherwise they'd be stranded on an empty
            picker. */}
        <PayWithStripeDialog
          open={newCardOpen}
          mode="pay_and_activate"
          companyId={this.props.companyId}
          amount={this.props.amount}
          paymentId={this.props.paymentId}
          onClose={() => {
            this.setSafe({ newCardOpen: false });
            // If the user closed the new-card dialog WITHOUT paying AND
            // they have no saved cards to fall back on, also close the
            // outer picker so they aren't stranded on an empty list.
            // After a successful payment `done` is true and the outer
            // picker shows its own "Payment successful" state, so we
            // intentionally don't close it here in that case.
            if (
              !this.state.done &&
              this.state.methods.length === 0 &&
              this.props.onClose
            ) {
              this.props.onClose();
            }
          }}
          onSuccess={(payload) => {
            // Hand off to the outer picker's success state instead of
            // tearing the inner dialog down silently. Setting done=true
            // makes SavedCardPaymentDialog render its CheckCircle +
            // "Payment successful" + Done button — without this the
            // PayWithStripeDialog vanishes the moment the poll returns
            // activated, leaving the user staring at a blank picker
            // wondering whether the charge went through.
            this.setSafe({ newCardOpen: false, done: true });
            if (this.props.onSuccess) this.props.onSuccess(payload);
          }}
        />
      </>
    );
  }
}

export default SavedCardPaymentDialog;
